import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { UserStoriesV2Actions } from '../actions/userstoriesv2.action';
import { catchError, EMPTY, finalize, map, of, switchMap, tap } from 'rxjs';
import { AppLoaderService } from 'src/app/services/app-loader.service';
import { ApiConstants } from 'src/app/base/api-constants';
import { AppGlobalService } from 'src/app/services/app.global.service';

@Injectable()
export class UserStoriesV2Effects {
  private actions = inject(Actions);
  private httpService = inject(HttpBaseService);
  private loaderService = inject(AppLoaderService);
  private appGlobalService = inject(AppGlobalService);

  getProcessHierarchy$ = createEffect(() =>
    this.actions.pipe(
      ofType(UserStoriesV2Actions.getProcessHierarchy),
      tap(() => this.loaderService.show()),
      switchMap((action: any) => {
        const { apiConstants, urlPathVariables } = action;

        return this.httpService[apiConstants.method](apiConstants, urlPathVariables).pipe(
          map((response: any) => {
            console.log('API response for getProcessHierarchy:', response);
            return UserStoriesV2Actions.setProcessHierarchy({ processHierarchy: response });
          }),
          catchError((error) => {
            console.error('Error in getProcessHierarchy:', error);
            return EMPTY;
          }),
          finalize(() => this.loaderService.hide())
        );
      })
    )
  );

  createProcess$ = createEffect(() =>
    this.actions.pipe(
      ofType(UserStoriesV2Actions.createProcess),
      tap(() => this.loaderService.show()),
      switchMap((action: any) => {
        const { apiConstants, process } = action;

        return this.httpService[apiConstants.method](apiConstants, process).pipe(
          map((response: any) => {
            console.log('API response for createProcess:', response);
            return UserStoriesV2Actions.createProcessSuccess();
          }),
          catchError((error) => {
            console.error('Error in createProcess:', error);
            return of(UserStoriesV2Actions.createProcessFailure());
          }),
          finalize(() => this.loaderService.hide())
        );
      })
    )
  );

  createProcessSuccess$ = createEffect(() =>
    this.actions.pipe(
      ofType(UserStoriesV2Actions.createProcessSuccess),
      map(() => {
        return UserStoriesV2Actions.getProcessHierarchy({
          apiConstants: {
            ...ApiConstants.getProcessHierarchy
          },
          urlPathVariables: { appId: this.appGlobalService.get('appId') }
        });
      })
    )
  );

  createProcessFailure$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(UserStoriesV2Actions.createProcessFailure),
        map(() => EMPTY)
      ),
    { dispatch: false }
  );

  updateProcess$ = createEffect(() =>
    this.actions.pipe(
      ofType(UserStoriesV2Actions.updateProcess),
      tap(() => this.loaderService.show()),
      switchMap((action: any) => {
        const { apiConstants, process } = action;

        return this.httpService[apiConstants.method](apiConstants, process).pipe(
          map((response: any) => {
            console.log('API response for updateProcess:', response);
            return UserStoriesV2Actions.updateProcessToHierarchy({ process: response });
          }),
          catchError((error) => {
            console.error('Error in updateProcess:', error);
            return EMPTY;
          }),
          finalize(() => this.loaderService.hide())
        );
      })
    )
  );

  deleteProcess$ = createEffect(() =>
    this.actions.pipe(
      ofType(UserStoriesV2Actions.deleteProcess),
      tap(() => this.loaderService.show()),
      switchMap((action: any) => {
        const { apiConstants, urlPathVariables } = action;

        return this.httpService[apiConstants.method](apiConstants, urlPathVariables).pipe(
          map((response: any) => {
            console.log('API response for deleteProcess:', response);
            return UserStoriesV2Actions.deleteProcessFromHierarchy({ processId: urlPathVariables.processId });
          }),
          catchError((error) => {
            console.error('Error in deleteProcess:', error);
            return EMPTY;
          }),
          finalize(() => this.loaderService.hide())
        );
      })
    )
  );

  getUserStoriesForProcess$ = createEffect(() =>
    this.actions.pipe(
      ofType(UserStoriesV2Actions.getUserStoriesForProcess),
      tap(() => this.loaderService.show()),
      switchMap((action: any) => {
        const { apiConstants, urlPathVariables } = action;

        return this.httpService[apiConstants.method](apiConstants, urlPathVariables).pipe(
          map((response: any) => {
            console.log('API response for getUserStoriesForProcess:', response);
            return UserStoriesV2Actions.setUserStoriesForProcess({
              processId: urlPathVariables.processId,
              userStories: response
            });
          }),
          catchError((error) => {
            console.error('Error in getUserStoriesForProcess:', error);
            return EMPTY;
          }),
          finalize(() => this.loaderService.hide())
        );
      })
    )
  );

  createUserStory$ = createEffect(() =>
    this.actions.pipe(
      ofType(UserStoriesV2Actions.createUserStory),
      tap(() => this.loaderService.show()),
      switchMap((action: any) => {
        const { apiConstants, userStory } = action;

        return this.httpService[apiConstants.method](apiConstants, userStory).pipe(
          map((response: any) => {
            console.log('API response for createUserStory:', response);
            return UserStoriesV2Actions.createUserStorySuccess({ processId: userStory.parentNodeSid });
          }),
          catchError((error) => {
            console.error('Error in createUserStory:', error);
            return of(UserStoriesV2Actions.createUserStoryFailure());
          }),
          finalize(() => this.loaderService.hide())
        );
      })
    )
  );

  createUserStorySuccess$ = createEffect(() =>
    this.actions.pipe(
      ofType(UserStoriesV2Actions.createUserStorySuccess),
      map((action: any) => {
        const { processId }: { processId: string } = action;
        const appId: string = this.appGlobalService.get('appId');
        if (!processId) {
          return UserStoriesV2Actions.getProcessHierarchy({
            apiConstants: {
              ...ApiConstants.getProcessHierarchy
            },
            urlPathVariables: { appId }
          });
        }
        return UserStoriesV2Actions.getUserStoriesForProcess({
          apiConstants: { ...ApiConstants.getUserStoriesForProcess },
          urlPathVariables: { appId, processId }
        });
      })
    )
  );

  createUserStoryFailure$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(UserStoriesV2Actions.createUserStoryFailure),
        map(() => EMPTY)
      ),
    { dispatch: false }
  );

  updateUserStory$ = createEffect(() =>
    this.actions.pipe(
      ofType(UserStoriesV2Actions.updateUserStory),
      tap(() => this.loaderService.show()),
      switchMap((action: any) => {
        const { apiConstants, userStory } = action;

        return this.httpService[apiConstants.method](apiConstants, userStory).pipe(
          map((response: any) => {
            console.log('API response for updateUserStory:', response);
            if (!response.parentNodeSid) {
              return UserStoriesV2Actions.updateProcessToHierarchy({ process: response });
            }
            return UserStoriesV2Actions.updateUserStoryInProcess({ userStory: response });
          }),
          catchError((error) => {
            console.error('Error in updateUserStory:', error);
            return EMPTY;
          }),
          finalize(() => this.loaderService.hide())
        );
      })
    )
  );

  deleteUserStory$ = createEffect(() =>
    this.actions.pipe(
      ofType(UserStoriesV2Actions.deleteUserStory),
      tap(() => this.loaderService.show()),
      switchMap((action: any) => {
        const { apiConstants, userStory } = action;

        return this.httpService[apiConstants.method](apiConstants, { userStoryId: userStory.sid }).pipe(
          map((response: any) => {
            console.log('API response for deleteUserStory:', response);
            return UserStoriesV2Actions.deleteUserStoryFromProcess({ userStory });
          }),
          catchError((error) => {
            console.error('Error in deleteUserStory:', error);
            return EMPTY;
          }),
          finalize(() => this.loaderService.hide())
        );
      })
    )
  );
}
